import React, { Component } from "react";
import qs from "qs"; // A querystring parsing and stringifying library with some added security.
import _ from "lodash";
import mixpanel from "mixpanel-browser";
import ClinicSignupView from "./ClinicSignupView";
import {
  strings,
  strongRegularExp,
  isValidMailAddress,
  history,
  decryptId,
  setPageMetaDesc,
  doctorIntentParams,
  userTimezone,
  getBrowserDetails,
  onlyAlphabetsWithSpace,
  setPhoneLimit,
  showErrorNotification,
  handleRedirectUserToSelectedPage
} from "../../../helpers";
import { getNewsDetails, signup, patientSignup } from "../../../services";
import ClinicSignupIntentView from "./ClinicSignupIntentView";
import { defaultCountryObj } from "../../../helpers/CountryCodeOptions";
import moment from "moment";
import momentTZ from "moment-timezone";

let seconds = 0; // declared globally to count seconds
class ClinicSignupContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      phoneNumber: "",
      password: "",
      doctorName: "",
      firstName: "",
      lastName: "",
      feError: false,
      feErrorMsg: "",
      serverError: false,
      submitErrors: [],
      screenType: "big",
      forStaff: false,
      userEmail: "",
      inValidEmail: false,
      utmSource: null,
      utmMedium: null,
      utmContent: null,
      utmCampaign: null,
      inviteCode: null,
      docId: null,
      newsId: null,
      newsLink: null,
      signupIntent: null,
      showTestView: false,
      testValue: null,
      signupSuccess: false,
      countryCode: defaultCountryObj?.country_code,
      isProcessing: false,
      signedUpAs: "doctor",
      staffAccPreselected: false,
      accountTypePatient: false,
      phoneNumberLengthLimit: 10,
      alpha_2_code: defaultCountryObj?.alpha_2_code,
      accountTypeHospital: false
    };
  }

  componentDidMount() {
    // The following code will be used to setup the meta information for sign up page
    this.handleSetMetaContent();
    // following code handles setting screen type to handle responsive changes
    window.addEventListener("resize", this.setScreenType);
    // fetch and set screen settings at load
    this.setScreenType();
    // the following code handles fetching any querystring parameters in the url,
    // so that the parameters can be stored in database, also this is function handles retaining the url parameters
    // accross the other url also
    this.fetchUTMParams();
    // log mixpanel event
    mixpanel.track("Signup Page Viewed");
  }

  componentWillUnmount() {
    // Always deattach event listener to prevent memory leaks
    window.removeEventListener("resize", this.setScreenType);
  }

  /**
   * @function handleSetMetaContent
   * @param nothing
   * @desc Function handles setting the title to the sign up page, for SEO purpose.
   * This function handles setting meta content for a page
   */
  handleSetMetaContent = () => {
    document.title = "Sign Up";
    // set meta description for login page
    setPageMetaDesc("Create an account");
  };

  /**
   * @function clinicSignupAction
   * @param "username", "password", "doctorName", "email", "utmParams", "claimDocId", "signupIntent", "firstName", "lastName"
   * @desc This function handles signing up the patient or a provider, i.e. handles signing up a patient or a provider,
   * based upon whether account type == "Patient" is chosen or not.
   */
  clinicSignupAction = (
    username,
    password,
    doctorName,
    email,
    utmParams,
    claimDocId,
    signupIntent,
    firstName,
    lastName
  ) => {
    const {
      signedUpAs,
      accountTypePatient,
      countryCode,
      alpha_2_code
    } = this.state;
    let params = null;
    this.setState({
      isProcessing: true
    });
    if (accountTypePatient) {
      params = {
        user_signup_form: {
          phone_number: username,
          country_code: countryCode,
          alpha_2_code: alpha_2_code, // alpha_2_code for selected country, decided according to country code
          password,
          email,
          signed_up_as: "patient",
          time_zone: userTimezone(), // fetch user timezone
          account: "patient",
          platform: "patient_web",
          name: doctorName
        },
        mixpanel_data: getBrowserDetails() // handle fetch browser details, to that user device details can be stored in mixpanel
      };
      this.setState({
        isProcessing: true
      });
      // service used to handle patient signup
      patientSignup(params).then(response => {
        this.setState({
          isProcessing: false
        });
        if (response?.data?.success) {
          mixpanel.track("Account Selected", { type: "Patient" });
          // signupSuccess is a state variable, that is used to show/ hide the verify user via OTP view
          this.setState({
            signupSuccess: true
          });
        } else {
          // process errors from api
          this.processErrors(response);
        }
      });
    } else {
      let mixpanelEventType = "";
      let signUpAs = "general";
      // check if intent is present and if present then does the intent is for CMS, doctor or growth
      // Following code decides setting signUpAs parameter, to be stored in db
      if (signupIntent) {
        if (doctorIntentParams.includes(signupIntent)) signUpAs = "doctor";
        else signUpAs = "general";
      } else {
        signUpAs = signedUpAs === "psychsignup" ? "general" : signedUpAs;
        if (signedUpAs === "psychsignup") signupIntent = "psychsignup";
      }
      // service used to handle provider signup
      signup(
        username,
        password,
        doctorName,
        email,
        utmParams,
        claimDocId,
        signUpAs,
        signupIntent,
        firstName,
        lastName,
        countryCode,
        alpha_2_code
      ).then(response => {
        this.setState({
          isProcessing: false
        });
        if (response?.data?.success) {
          let eventType = "";
          if (signedUpAs === "clinic_staff") {
            mixpanelEventType = "staff";
          } else if (signedUpAs === "psychsignup") {
            mixpanelEventType = "therapist";
          } else {
            mixpanelEventType = signedUpAs;
          }
          mixpanel.track("Account Selected", { type: mixpanelEventType });

          // check if intent is present and if present then does the intent is for CMS, doctor or growth
          if (utmParams && utmParams.utm_campaign == "news") {
            eventType = "News Feed";
          }
          this.setState({
            signupSuccess: true,
            eventType
          });
        } else {
          // Process errors from api
          this.processErrors(response);
        }
      });
    }
  };

  /**
   * @function processErrors
   * @param response
   * @desc This handles setting state
   *  of variable which
   * is to be shown to the user
   *  when api returns some error
   */
  processErrors = response => {
    const { errors } = response.data;
    let error = errors;
    if (Array.isArray(errors)) {
      // eslint-disable-next-line prefer-destructuring
      error = errors[0];
    }
    this.setState({
      feError: true,
      errorText:
        error.source === "staff_invite"
          ? "Looks like you are not invited by any clinic, you need to have staff invite."
          : error.title
    });
  };

  /**
   * @function fetchUTMParams
   * @desc This funtion fetches utm params in url
   */
  fetchUTMParams = () => {
    const params = qs.parse(this.props.location.search.slice(1)); // parse querystring parameters from the current url

    if (params && !_.isEmpty(params)) {
      // fetch utm parameters from querystring
      const {
        utm_source,
        utm_medium,
        utm_content,
        utm_campaign,
        kic,
        dname,
        did,
        nid,
        su_int,
        a6t,
        acc,
        ph
      } = params;
      // set state of utm parameters
      this.setState({
        utmSource: utm_source || null,
        utmMedium: utm_medium || null,
        utmContent: utm_content || null,
        utmCampaign: utm_campaign || null,
        inviteCode: kic || null,
        doctorName: dname || "",
        docId: did ? decryptId(did) : null,
        newsId: nid || null,
        signupIntent: su_int || null,
        showTestView: a6t ? true : false,
        testValue: a6t || null,
        signedUpAs: acc === "staff" ? "clinic_staff" : "doctor",
        staffAccPreselected: acc === "staff" ? true : false,
        accountTypePatient: acc === "patient" || false,
        accountTypeHospital: acc === "hospital" || false,
        phoneNumber: parseInt(ph, 10) || null
      });
      // when doctor share news
      if (nid) {
        this.getNewsDetails(nid);
      }
    }
  };

  /**
   * @function setScreenType
   * @desc This funtion sets the screen type
   * in local state, this is used for responsive issues for UI
   */
  setScreenType = () => {
    const windowWidth = window.innerWidth;
    const screenType = windowWidth > 992 ? "big" : "small";
    this.setState({ screenType });
  };

  /**
   * @function handleChange
   * @param e
   * @param nothing
   * @desc This handles input field changes, any entry done in input fields is captured with this function in the state variables
   */
  handleChange = e => {
    const { name, value } = e.target;

    this.setState({
      [name]: name === "doctorName" ? value : value.replace(/\s/g, ""),
      feError: false,
      serverError: false,
      submitErrors: []
    });
  };

  /**
   * @function checkPasswordStrength
   * @param value
   * @param nothing
   * @desc This checks the strength of the
   *  password entered by the user in percentage
   */
  checkPasswordStrength = value => {
    // check if password is strong enough to be used
    return strongRegularExp.test(value);
  };

  /**
   * @function validateForm
   * @desc This function will validate form
   */

  validateForm = () => {
    const { phoneNumber, password, userEmail, doctorName } = this.state;
    let feError = false;
    let feErrorMsg = "";
    const reg = /^-?(0|[1-9][0-9]*)(\.[0-9]*)?$/;
    if (this.state.showTestView) {
      if (!this.state.firstName) {
        feError = true;
        feErrorMsg = "Enter your name";
      }
    }
    if (phoneNumber === null || phoneNumber.trim() === "") {
      feError = true;
      feErrorMsg = "phoneErrorMsg";
    } else if (phoneNumber && phoneNumber.trim() && !reg.test(phoneNumber)) {
      feError = true;
      feErrorMsg = "required_phone_number";
    } else if (
      userEmail === null ||
      userEmail.trim() === "" ||
      !isValidMailAddress(userEmail)
    ) {
      feError = true;
      feErrorMsg = "enter_valid_email";
    } else if (password === null || password.trim() === "") {
      feError = true;
      feErrorMsg = "password_required";
    } else if (
      (password !== null &&
        password.trim() !== "" &&
        !this.checkPasswordStrength(password)) ||
      password.length < 8
    ) {
      feError = true;
      feErrorMsg = "follow_pwd_hint";
    }

    if (doctorName && !onlyAlphabetsWithSpace.test(doctorName)) {
      feError = true;
      feErrorMsg = "nameError";
    }
    this.setState({
      feError,
      feErrorMsg
    });
    return feError;
  };

  /**
   * @function handleSubmit
   * @param e
   * @param nothing
   * @desc This handles the submission of login parameters, calls sign up action
   */
  handleSubmit = e => {
    e.preventDefault();
    const {
      phoneNumber,
      password,
      doctorName,
      firstName,
      lastName,
      userEmail,
      utmSource,
      utmMedium,
      utmContent,
      utmCampaign,
      inviteCode,
      docId,
      signupIntent
    } = this.state;
    // validate the form for all errors
    const formError = this.validateForm();
    if (!formError) {
      this.setState({
        feError: false,
        feErrorMsg: ""
      });

      // Sign up user
      if (navigator && navigator.onLine) {
        // to show the timer
        this.onTimer();
        seconds = 0;
        if (utmSource || utmMedium || utmContent || utmCampaign) {
          const utmParams = {
            utm_source: utmSource,
            utm_medium: utmMedium,
            utm_campaign: utmCampaign,
            utm_content: utmContent,
            param_type: "utm"
          };
          this.clinicSignupAction(
            phoneNumber,
            password,
            doctorName,
            userEmail,
            utmParams,
            docId,
            signupIntent,
            firstName,
            lastName
          );
        } else if (inviteCode) {
          const inviteParam = {
            invite_code: inviteCode,
            param_type: "invite"
          };
          this.clinicSignupAction(
            phoneNumber,
            password,
            doctorName,
            userEmail,
            inviteParam,
            docId,
            signupIntent,
            firstName,
            lastName
          );
        } else {
          this.clinicSignupAction(
            phoneNumber,
            password,
            doctorName,
            userEmail,
            null,
            docId,
            signupIntent,
            firstName,
            lastName
          );
        }
      } else {
        showErrorNotification("Please check your network connection");
      }
    } else {
      return;
    }
  };

  /**
   * @function onTimer
   * @desc This method starts timer after 5 sec,
   * and runs every 5sec to check if processing state has changed or not
   */
  onTimer = () => {
    const timer = setInterval(() => {
      if (!this.state.isProcessing) {
        seconds = 0;
        clearInterval(timer);
      } else {
        seconds++;
      }
    }, 5000);
  };

  /**
   * @function phoneNumberHint
   * @desc This handles showing phone number input hint
   */
  phoneNumberHint = () => {
    return (
      <div>
        <p>{strings.phoneNumberHint1}</p>
        <p>{strings.phoneNumberHint2}</p>
      </div>
    );
  };

  /**
   * @function goToSignIn
   * @desc This function handles redirection to given page
   */
  goToSignIn = e => {
    if (e) e.preventDefault();
    let redirectUrl = "signin";
    const {
      utmSource,
      utmMedium,
      utmContent,
      utmCampaign,
      inviteCode,
      doctorName,
      docId,
      newsId,
      signupIntent,
      testValue,
      accountTypePatient,
      accountTypeHospital
    } = this.state;
    const params = {
      utmSource,
      utmMedium,
      utmContent,
      utmCampaign,
      inviteCode,
      doctorName,
      docId,
      newsId,
      signupIntent,
      testValue,
      accountTypePatient,
      accountTypeHospital
    };
    handleRedirectUserToSelectedPage(redirectUrl, params);
  };

  /**
   * @function getNewsDetails
   * @desc This function gets news details
   */
  getNewsDetails = newsId => {
    if (newsId) {
      getNewsDetails(newsId).then(response => {
        if (response.data.success) {
          this.setState({ newsLink: response.data.data.source_link });
        }
      });
    }
  };

  /**
   * @function handleNewsClickEvent
   * @desc This function handles mixpanel event
   */
  handleNewsClickEvent = () => {
    mixpanel.track("News Viewed");
  };

  /**
   * @function handleSelectProvider
   * @desc This function handles setting the provider type shown in Select provider dropdown in the state variable
   */
  handleSelectProvider = value => {
    this.setState({ signedUpAs: value });
  };

  handleCodeChange = (countryCode, countryCodeObj) => {
    this.setState({
      phoneNumberLengthLimit: setPhoneLimit[countryCode],
      alpha_2_code: countryCodeObj?.props?.countryCodeObj?.alpha_2_code,
      countryCode
    });
  };

  render() {
    const {
      newsId,
      newsLink,
      utmCampaign,
      signupIntent,
      showTestView,
      signupSuccess,
      countryCode,
      errorText,
      signedUpAs,
      staffAccPreselected,
      accountTypePatient,
      phoneNumberLengthLimit,
      alpha_2_code,
      phoneNumber,
      accountTypeHospital
    } = this.state;
    return (
      <>
        {showTestView ? (
          <ClinicSignupIntentView
            handleChange={this.handleChange}
            handleSubmit={this.handleSubmit}
            isProcessing={this.state.isProcessing}
            feError={this.state.feError}
            feErrorMsg={this.state.feErrorMsg}
            phoneNumber={phoneNumber}
            password={this.state.password}
            firstName={this.state.firstName}
            lastName={this.state.lastName}
            serverError={this.state.serverError}
            submitErrors={this.state.submitErrors}
            screenType={this.state.screenType}
            phoneNumberHint={this.phoneNumberHint}
            checkPasswordStrength={this.checkPasswordStrength}
            userEmail={this.state.userEmail}
            inValidEmail={this.state.inValidEmail}
            goToSignIn={this.goToSignIn}
            newsId={newsId}
            newsLink={newsLink}
            handleNewsClickEvent={this.handleNewsClickEvent}
            utmCampaign={utmCampaign}
            signupIntent={signupIntent}
            errorText={errorText}
            signupSuccess={signupSuccess}
          />
        ) : (
          <ClinicSignupView
            handleChange={this.handleChange}
            handleSubmit={this.handleSubmit}
            isProcessing={this.state.isProcessing}
            feError={this.state.feError}
            feErrorMsg={this.state.feErrorMsg}
            phoneNumber={phoneNumber}
            password={this.state.password}
            doctorName={this.state.doctorName}
            serverError={this.state.serverError}
            submitErrors={this.state.submitErrors}
            screenType={this.state.screenType}
            forStaff={this.state.forStaff}
            phoneNumberHint={this.phoneNumberHint}
            userEmail={this.state.userEmail}
            inValidEmail={this.state.inValidEmail}
            goToSignIn={this.goToSignIn}
            signupSuccess={signupSuccess}
            countryCode={countryCode}
            eventType={this.state.eventType}
            errorText={errorText}
            handleSelectProvider={this.handleSelectProvider}
            signedUpAs={signedUpAs}
            staffAccPreselected={staffAccPreselected}
            accountTypePatient={accountTypePatient}
            handleCodeChange={this.handleCodeChange}
            phoneNumberLengthLimit={phoneNumberLengthLimit}
            alpha_2_code={alpha_2_code}
            accountTypeHospital={accountTypeHospital}
          />
        )}
      </>
    );
  }
}

export default ClinicSignupContainer;
